'use client'

import { Suspense, useState, useEffect } from 'react'
import { Logo } from '@/components/logo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AuthBackground } from '@/components/ui/auth-background'
import { useAuthForm } from '@/hooks/use-auth-form'
import { Loader2, Eye, EyeOff, ArrowRight, Users, Zap, Shield } from 'lucide-react'
import Link from 'next/link'
import { DotPattern } from '@/components/magicui/dot-pattern'
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/animate-ui/radix/tooltip'
import { useSearchParams } from 'next/navigation'
import { toast } from 'sonner'

export default function LoginPageWrapper() {
    return (
        <Suspense fallback={<div>Loading...</div>}>
            <LoginPage />
        </Suspense>
    )
}

function LoginPage() {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const { loading, handleLogin, handleGoogleSignIn } = useAuthForm()
    const searchParams = useSearchParams()

    useEffect(() => {
        const message = searchParams?.get('message')
        if (message) {
            toast.info(message)
        }
    }, [searchParams])

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        await handleLogin(email, password)
    }

    return (
        <>
            {/* Simplified Dot Pattern Background - No glow for better performance */}
            <DotPattern
                width={24}
                height={24}
                cx={1}
                cy={1}
                cr={1}
                glow={false}
                className="fill-neutral-400/15 [mask-image:radial-gradient(800px_circle_at_center,white,transparent)]"
            />

            <AuthBackground
                spotlightIntensity="subtle"
                focusPosition="center"
                animationSpeed="slow"
                colorScheme="purple-blue"
                enableFormFocus={true}
                enableAccentGradients={false}
            />

            <section className="relative min-h-screen bg-white flex will-change-transform">
                {/* Left Side - Branding & Features - Hidden on mobile */}
                <div className="hidden lg:flex flex-1 lg:flex-[2] p-8 lg:p-16 flex-col justify-center relative overflow-hidden">
                    {/* Floating Audience Tag (doesn't affect layout) */}
                    <div className="absolute top-8 left-8 z-10 flex items-center gap-3">
                        <div className="bg-[#facc15] border-4 border-black px-3 py-1 transform rotate-1">
                            <span className="font-black text-black text-xs tracking-widest">FOR EVENT HOSTS</span>
                        </div>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        className="bg-black text-white border-4 border-black px-3 py-1 text-xs font-black transform rotate-1 hover:rotate-0"
                                    >
                                        Not a host?
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-xs">
                                    <div className="space-y-2">
                                        <p className="font-bold">Want to try the guest experience?</p>
                                        <Link href="/guest-static/" className="inline-block bg-white text-black border-2 border-black px-2 py-1 font-black">
                                            Open Guest Demo
                                        </Link>
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                    {/* Logo Section */}
                    <div className="mb-12">
                        <Link href="/" aria-label="go home" className="inline-block mb-8">
                            <div className="bg-yellow-400 border-8 border-black p-6 transform -rotate-2 hover:rotate-0 transition-transform duration-200">
                                <Logo height={60} uniColor />
                            </div>
                        </Link>
                        
                        <div className="bg-blue-400 border-8 border-black p-8 mb-8">
                            <h1 className="text-5xl lg:text-7xl font-black text-black mb-4">
                                WELCOME BACK,<br />EVENT HOST!
                            </h1>
                            <p className="text-xl lg:text-2xl font-bold text-black">
                                READY TO RUN YOUR NEXT EVENT?
                            </p>
                        </div>
                    </div>

                    {/* Feature Highlights */}
                    <div className="space-y-6">
                        <div className="bg-green-400 border-4 border-black p-6">
                            <div className="flex items-center gap-4">
                                <div className="bg-black p-3 border-4 border-black">
                                    <Users className="w-8 h-8 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-black text-black">ENGAGE ATTENDEES</h3>
                                    <p className="font-bold text-black">Run icebreakers and prompts that get rooms talking</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-purple-400 border-4 border-black p-6">
                            <div className="flex items-center gap-4">
                                <div className="bg-black p-3 border-4 border-black">
                                    <Zap className="w-8 h-8 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-black text-black">SMART MATCHMAKING</h3>
                                    <p className="font-bold text-black">Match attendees by goals, roles, or interests</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-orange-400 border-4 border-black p-6">
                            <div className="flex items-center gap-4">
                                <div className="bg-black p-3 border-4 border-black">
                                    <Shield className="w-8 h-8 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-black text-black">TRUSTED & SECURE</h3>
                                    <p className="font-bold text-black">Control access and protect attendee data</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Stats */}
                    <div className="mt-12 grid grid-cols-3 gap-4">
                        <div className="bg-pink-400 border-4 border-black p-4 text-center transform rotate-2 hover:rotate-0 transition-transform duration-200">
                            <div className="text-3xl font-black text-black">10K+</div>
                            <div className="font-bold text-black text-sm">ACTIVE USERS</div>
                        </div>
                        <div className="bg-red-400 border-4 border-black p-4 text-center transform -rotate-1 hover:rotate-0 transition-transform duration-200">
                            <div className="text-3xl font-black text-black">95%</div>
                            <div className="font-bold text-black text-sm">SUCCESS RATE</div>
                        </div>
                        <div className="bg-yellow-400 border-4 border-black p-4 text-center transform rotate-1 hover:rotate-0 transition-transform duration-200">
                            <div className="text-3xl font-black text-black">24/7</div>
                            <div className="font-bold text-black text-sm">SUPPORT</div>
                        </div>
                    </div>
                </div>

                {/* Right Side - Login Form - Full width on mobile */}
                <div className="w-full lg:w-96 xl:w-[28rem] bg-gray-50 lg:border-l-8 border-black p-4 sm:p-8 flex flex-col justify-center min-h-screen lg:min-h-0">
                    {/* Mobile Header - Only visible on mobile */}
                    <div className="lg:hidden mb-8 space-y-4">
                        {/* Mobile Tags - Responsive Layout */}
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
                            <div className="bg-[#facc15] border-2 sm:border-4 border-black px-2 sm:px-3 py-1 transform rotate-1">
                                <span className="font-black text-black text-xs sm:text-sm tracking-widest">FOR EVENT HOSTS</span>
                            </div>
                            {/* Mobile: Direct link, Desktop: Tooltip */}
                            <div className="relative group">
                                {/* Mobile - Direct link */}
                                <Link 
                                    href="/guest-static/" 
                                    className="sm:hidden bg-black text-white border-2 border-black px-2 py-1 text-xs font-black transform rotate-1 hover:rotate-0 transition-transform inline-block"
                                >
                                    Not a host?
                                </Link>
                                
                                {/* Desktop - Tooltip */}
                                <div className="hidden sm:block">
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <button type="button" className="bg-black text-white border-2 sm:border-4 border-black px-2 py-1 text-xs sm:text-sm font-black transform rotate-1 hover:rotate-0 transition-transform">
                                                    Not a host?
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent side="bottom" className="max-w-xs">
                                                <div className="space-y-2">
                                                    <p className="font-bold">Want to try the guest experience?</p>
                                                    <Link href="/guest-static/" className="inline-block bg-white text-black border-2 border-black px-2 py-1 font-black text-xs">
                                                        Open Guest Demo
                                                    </Link>
                                                </div>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            </div>
                        </div>
                        
                        {/* Mobile Logo */}
                        <div className="text-center">
                            <Link href="/" aria-label="go home" className="inline-block">
                                <div className="bg-black border-2 sm:border-4 border-black p-3 sm:p-4 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200">
                                    <Logo className="h-8 sm:h-10 w-auto text-white" uniColor />
                                </div>
                            </Link>
                            <p className="mt-3 text-base sm:text-lg font-bold text-black">HOST • ENGAGE • GROW</p>
                        </div>
                    </div>

                    {/* Header */}
                    <div className="bg-yellow-400 border-4 lg:border-8 border-black p-4 lg:p-6 transform -rotate-1 hover:rotate-0 transition-transform duration-200 mb-6 lg:mb-8">
                        <h2 className="text-2xl lg:text-3xl font-black text-black mb-2">SIGN IN</h2>
                        <p className="text-base lg:text-lg font-bold text-black">LET'S GET YOU LOGGED IN!</p>
                    </div>

                    {/* Social Login */}
                    <div className="bg-blue-400 border-4 border-black p-4 lg:p-6 transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-4 lg:mb-6">
                        <h3 className="text-base lg:text-lg font-black text-black mb-3 lg:mb-4">QUICK LOGIN</h3>
                        <div className="space-y-3">
                            <Button
                                type="button"
                                onClick={handleGoogleSignIn}
                                disabled={loading}
                                className="w-full bg-white border-4 border-black text-black font-black hover:bg-black hover:text-white transition-all duration-200 transform hover:rotate-1"
                            >
                                {loading ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="18"
                                        height="18"
                                        viewBox="0 0 256 262"
                                        className="mr-2"
                                    >
                                        <path
                                            fill="#4285f4"
                                            d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.690 42.356l-.244 1.622l38.755 30.023l2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"></path>
                                        <path
                                            fill="#34a853"
                                            d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055c-34.523 0-63.824-22.773-74.269-54.25l-1.531.13l-40.298 31.187l-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"></path>
                                        <path
                                            fill="#fbbc05"
                                            d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82c0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602z"></path>
                                        <path
                                            fill="#eb4335"
                                            d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0C79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"></path>
                                    </svg>
                                )}
                                CONTINUE WITH GOOGLE
                            </Button>
                            <Button
                                type="button"
                                className="w-full bg-white border-4 border-black text-black font-black hover:bg-black hover:text-white transition-all duration-200 transform hover:-rotate-1"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="18"
                                    height="18"
                                    viewBox="0 0 256 256"
                                    className="mr-2"
                                >
                                    <path fill="#f1511b" d="M121.666 121.666H0V0h121.666z"></path>
                                    <path fill="#80cc28" d="M256 121.666H134.335V0H256z"></path>
                                    <path fill="#00adef" d="M121.663 256.002H0V134.336h121.663z"></path>
                                    <path fill="#fbbc09" d="M256 256.002H134.335V134.336H256z"></path>
                                </svg>
                                CONTINUE WITH MICROSOFT
                            </Button>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="relative mb-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t-4 border-black"></div>
                        </div>
                        <div className="relative flex justify-center">
                            <div className="bg-white px-4 font-black text-black">OR</div>
                        </div>
                    </div>

                    {/* Email Form */}
                    <form
                        onSubmit={onSubmit}
                        className="bg-green-400 border-4 border-black p-4 lg:p-6 transform -rotate-1 hover:rotate-0 transition-transform duration-200 mb-4 lg:mb-6 will-change-transform"
                    >
                        <div className="space-y-3 lg:space-y-4">
                            {/* Email Field */}
                            <div>
                                <Label htmlFor="email" className="block text-base lg:text-lg font-black text-black mb-2">
                                    EMAIL
                                </Label>
                                <Input
                                    type="email"
                                    required
                                    name="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading}
                                    placeholder="your@email.com"
                                    className="border-4 border-black bg-white font-bold text-black placeholder:text-black/60 placeholder:font-bold focus:bg-yellow-400 focus:border-6 transition-colors duration-150 text-base lg:text-lg p-2 lg:p-3 will-change-auto"
                                />
                            </div>

                            {/* Password Field */}
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <Label htmlFor="password" className="text-base lg:text-lg font-black text-black">
                                        PASSWORD
                                    </Label>
                                    <Link
                                        href="/forgot-password"
                                        className="bg-red-400 border-2 border-black px-2 py-1 font-black text-black hover:bg-black hover:text-white transition-all duration-200 text-xs transform rotate-1 hover:rotate-0"
                                    >
                                        FORGOT?
                                    </Link>
                                </div>
                                <div className="relative">
                                    <Input
                                        type={showPassword ? "text" : "password"}
                                        required
                                        name="password"
                                        id="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={loading}
                                        placeholder="your password"
                                        className="border-4 border-black bg-white font-bold text-black placeholder:text-black/60 placeholder:font-bold focus:bg-yellow-400 focus:border-6 transition-colors duration-150 text-base lg:text-lg p-2 lg:p-3 pr-10 lg:pr-12 will-change-auto"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black hover:text-gray-600 transition-colors"
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <Button 
                                type="submit" 
                                disabled={loading}
                                className="w-full bg-black text-white font-black text-base lg:text-lg p-2 lg:p-3 border-4 border-black hover:bg-white hover:text-black transition-colors duration-150 transform hover:rotate-1 group will-change-transform"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                        SIGNING IN...
                                    </>
                                ) : (
                                    <>
                                        SIGN IN NOW!
                                        <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>

                    {/* Sign Up Link */}
                    <div className="bg-purple-400 border-4 border-black p-3 lg:p-4 text-center transform rotate-1 hover:rotate-0 transition-transform duration-200">
                        <p className="text-base lg:text-lg font-black text-black mb-2">
                            NEW TO SOCIALLOOP?
                        </p>
                        <Link
                            href="/signup"
                            className="bg-black text-white px-4 lg:px-6 py-2 inline-block border-4 border-black hover:bg-white hover:text-black transition-all duration-200 transform -rotate-1 hover:rotate-0 font-black text-sm lg:text-base"
                        >
                            CREATE ACCOUNT
                        </Link>
                    </div>
                </div>
            </section>
        </>
    )
}